import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const ServicesDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { item } = location.state || {};
  const [selectedVideo, setSelectedVideo] = useState(null); // 🎥 For modal video control

  if (!item) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[#030303] text-white">
        <h1 className="text-3xl font-bold mb-4 text-blue-400">
          No Service Selected
        </h1>
        <button
          onClick={() => navigate("/services")}
          className="px-5 py-2 bg-blue-600 hover:bg-blue-500 rounded-md transition-all duration-300"
        >
          Back to Services
        </button>
      </div>
    );
  }

  return (
    <section className="min-h-screen bg-gradient-to-b from-[#030303] to-[#101010] text-white px-6 md:px-20 py-16">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        <h1 className="text-5xl font-extrabold text-blue-500 mb-4">
          {item.name}
        </h1>
        <p className="text-gray-300 text-lg max-w-2xl mx-auto">{item.summary}</p>
      </motion.div>

      {/* Details Card */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-5xl mx-auto bg-[#1a1a1a] p-10 rounded-3xl shadow-lg shadow-blue-900/30 relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-tr from-blue-700/10 to-blue-500/5 blur-3xl"></div>

        <div className="relative z-10">
          <h2 className="text-2xl font-semibold mb-4 text-yellow-400">
            What We Offer
          </h2>
          <p className="text-gray-300 leading-relaxed mb-6">
            {item.details ||
              `Our ${item.name.toLowerCase()} service combines creativity, strategy, and innovation to deliver exceptional results.`}
          </p>

          {/* Components Section */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-blue-400 mb-4">
              Key Components
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: "Web Development",
                  desc: "We build modern, fast, and scalable websites that deliver exceptional performance.",
                  tech: "HTML, CSS, JavaScript, React, Node.js",
                },
                {
                  title: "App Development",
                  desc: "Powerful, secure, and scalable mobile applications built for iOS and Android.",
                  tech: "Flutter, React Native, Firebase, Swift",
                },
                {
                  title: "UI/UX Design",
                  desc: "Crafting user-centered designs that ensure delightful user experiences.",
                  tech: "Figma, Adobe XD, Sketch, Illustrator",
                },
                {
                  title: "Branding & Strategy",
                  desc: "Creating strong brand identities that make lasting impressions.",
                  tech: "Logo Design, Typography, Guidelines",
                },
              ].map((comp, i) => (
                <div
                  key={i}
                  className="bg-[#202020] rounded-2xl p-6 hover:scale-105 transition-transform duration-300"
                >
                  <h4 className="text-lg font-semibold text-yellow-400 mb-2">
                    {comp.title}
                  </h4>
                  <p className="text-gray-300 text-sm mb-4">{comp.desc}</p>
                  <div className="text-xs text-gray-400">
                    <strong>Technologies:</strong> {comp.tech}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Gallery Section */}
          {item.images && item.images.length > 0 && (
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-blue-400 mb-4">Gallery</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {item.images.map((img, i) => (
                  <div key={i} className="group relative overflow-hidden rounded-lg border-2 border-blue-500 hover:border-blue-400 transition-all duration-300">
                    <img
                      src={img}
                      alt={`${item.name} project showcase ${i + 1}`}
                      className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="text-center text-white">
                        <p className="text-sm font-medium">Project Image {i + 1}</p>
                        <p className="text-xs opacity-80 mt-1">Click to view full size</p>
                      </div>
                    </div>
                    <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                      Image {i + 1} of {item.images.length}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-gray-400 text-sm mt-4 text-center">
                Explore our {item.name.toLowerCase()} project gallery showcasing real work samples and creative implementations.
              </p>
            </div>
          )}

          {/* Videos Section (with Lightbox) */}
          {item.videos && item.videos.length > 0 && (
            <div className="mb-8 max-w-full">
              <h3 className="text-xl font-semibold text-blue-400 mb-4">Videos</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {item.videos.map((video, i) => (
                  <div
                    key={i}
                    onClick={() => setSelectedVideo(video)}
                    className="relative cursor-pointer group overflow-hidden rounded-lg border-2 border-blue-500 hover:border-blue-400 transition-all duration-300"
                  >
                    {video.includes("youtube.com") ? (
                      <img
                        src={`https://img.youtube.com/vi/${video.split("/embed/")[1]}/0.jpg`}
                        alt={`${item.name} demonstration video ${i + 1}`}
                        className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : (
                      <video
                        src={video}
                        className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                        muted
                      />
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="text-center text-white">
                        <div className="bg-blue-600 p-3 rounded-full mb-2">
                          ▶
                        </div>
                        <p className="text-sm font-medium">Play Video {i + 1}</p>
                        <p className="text-xs opacity-80 mt-1">Click to watch full demo</p>
                      </div>
                    </div>
                    <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                      Video {i + 1} of {item.videos.length}
                    </div>
                    <div className="absolute top-2 right-2 bg-blue-600/80 text-white text-xs px-2 py-1 rounded">
                      {video.includes("youtube.com") ? "YouTube" : "Local"}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-gray-400 text-sm mt-4 text-center">
                Watch our {item.name.toLowerCase()} video demonstrations and see our process in action.
              </p>
            </div>
          )}

          {/* Lightbox Modal */}
          <AnimatePresence>
            {selectedVideo && (
              <motion.div
                className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.div
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.8 }}
                  className="relative w-[90%] md:w-[60%]"
                >
                  <iframe
                    src={selectedVideo}
                    title="Selected Video"
                    className="w-full h-80 md:h-[450px] rounded-lg"
                    allowFullScreen
                  ></iframe>
                  <button
                    onClick={() => setSelectedVideo(null)}
                    className="absolute -top-8 right-0 text-white text-3xl hover:text-blue-400 transition-colors"
                  >
                    ✕
                  </button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Benefits Section */}
          <div className="grid md:grid-cols-2 gap-8 mt-8">
            <div className="bg-[#202020] rounded-2xl p-6 hover:scale-105 transition-transform duration-300">
              <h3 className="text-xl font-semibold text-blue-400 mb-2">
                Key Benefits
              </h3>
              <ul className="list-disc list-inside text-gray-300 space-y-2">
                <li>Strategic Planning and Execution</li>
                <li>Customized Design and Development</li>
                <li>Scalable and Secure Solutions</li>
                <li>Dedicated Ongoing Support</li>
              </ul>
            </div>

            <div className="bg-[#202020] rounded-2xl p-6 hover:scale-105 transition-transform duration-300">
              <h3 className="text-xl font-semibold text-blue-400 mb-2">
                Why Choose Us?
              </h3>
              <ul className="list-disc list-inside text-gray-300 space-y-2">
                <li>Experienced professionals in each domain</li>
                <li>Data-driven insights for smart decisions</li>
                <li>Commitment to client satisfaction</li>
                <li>Modern technologies & agile workflow</li>
              </ul>
            </div>
          </div>

          {/* CTA */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate("/contact")}
            className="mt-10 px-8 py-3 bg-blue-600 hover:bg-blue-500 rounded-md font-semibold text-white transition-all duration-300"
          >
            Get Started with {item.name}
          </motion.button>
        </div>
      </motion.div>
    </section>
  );
};

export default ServicesDetails;
